import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, X } from "lucide-react";

const rupee = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * TaxBlock — shared line-items + GST + TDS + summary block for
 * Project Expenses (vendor bill) and Reimbursement Claims.
 *
 * Props:
 *   vendor                  — { state, gst_registered, vendor_type, pan }
 *   rkfState                — string (place of supply default)
 *   gstRates                — [{ rate, label, is_active }]
 *   tdsSections             — [{ code, label, rate_individual_huf, rate_others, rate_no_pan, threshold_single }]
 *   value                   — { lines, tds_section_code, tds_rate, tds_override_reason, round_off, place_of_supply }
 *   onChange(next)          — bubble every change
 *   compactHeader           — bool; reimbursements pass true to hide vendor-derived rows
 */
export default function TaxBlock({ vendor, rkfState, gstRates = [], tdsSections = [], value, onChange, compactHeader = false, tdsReadonly = false }) {
  const [invoiceGst, setInvoiceGst] = useState(value?.lines?.[0]?.gst_rate ?? 0);
  const place = value.place_of_supply || rkfState || "Delhi";
  const gstReg = !!vendor?.gst_registered;
  const split = !gstReg ? "NONE" : (vendor?.state && place && vendor.state.toLowerCase() === place.toLowerCase() ? "CGST_SGST" : "IGST");

  // Compute totals live — mirrors backend compute_bill()
  const totals = useMemo(() => {
    let sub = 0, cgst = 0, sgst = 0, igst = 0;
    for (const ln of (value.lines || [])) {
      let amt = parseFloat(ln.amount || 0);
      const qty = parseFloat(ln.qty || 0), rate = parseFloat(ln.rate || 0);
      if (qty && rate) amt = Math.round(qty * rate * 100) / 100;
      const g = !gstReg ? 0 : parseFloat(ln.gst_rate || 0);
      const gAmt = Math.round(amt * g / 100 * 100) / 100;
      sub += amt;
      if (split === "CGST_SGST") {
        const half = Math.round(gAmt / 2 * 100) / 100;
        cgst += half; sgst += (gAmt - half);
      } else if (split === "IGST") {
        igst += gAmt;
      }
    }
    sub = Math.round(sub * 100) / 100;
    cgst = Math.round(cgst * 100) / 100; sgst = Math.round(sgst * 100) / 100; igst = Math.round(igst * 100) / 100;
    const totalGst = Math.round((cgst + sgst + igst) * 100) / 100;
    const invoiceTotal = Math.round((sub + totalGst) * 100) / 100;
    const rate = parseFloat(value.tds_rate || 0);
    const tdsAmount = Math.round(sub * rate / 100);
    const ro = Math.max(-1, Math.min(1, parseFloat(value.round_off || 0)));
    const netPayable = Math.round((invoiceTotal - tdsAmount + ro) * 100) / 100;
    return { sub, cgst, sgst, igst, totalGst, invoiceTotal, tdsAmount, netPayable };
  }, [value.lines, value.tds_rate, value.round_off, gstReg, split]);

  // Push totals up so parent can send ui_* fields on submit
  useEffect(() => {
    onChange({ ...value, _ui_totals: totals });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals.sub, totals.totalGst, totals.invoiceTotal, totals.tdsAmount, totals.netPayable]);

  const updLine = (i, k, v) => {
    const lines = [...(value.lines || [])];
    lines[i] = { ...lines[i], [k]: v };
    if (k === "qty" || k === "rate") {
      const qty = parseFloat(lines[i].qty || 0), rate = parseFloat(lines[i].rate || 0);
      if (qty && rate) lines[i].amount = Math.round(qty * rate * 100) / 100;
    }
    onChange({ ...value, lines });
  };
  const addLine = () => onChange({ ...value, lines: [...(value.lines || []), { description: "", qty: 0, rate: 0, amount: 0, gst_rate: invoiceGst }] });
  const rmLine = (i) => onChange({ ...value, lines: (value.lines || []).filter((_, ix) => ix !== i) });

  const applyInvoiceGst = (r) => {
    setInvoiceGst(r);
    onChange({ ...value, lines: (value.lines || []).map(l => ({ ...l, gst_rate: r })) });
  };

  // TDS section change → auto-fill rate from section master + vendor type
  const onTdsSection = (code) => {
    const s = tdsSections.find(x => x.code === code);
    let rate = 0;
    if (s) {
      const hasPan = !!(vendor?.pan);
      const vt = vendor?.vendor_type;
      if (!hasPan) rate = s.rate_no_pan || 20;
      else if (vt === "Individual" || vt === "HUF") rate = s.rate_individual_huf || 0;
      else rate = s.rate_others || 0;
    }
    onChange({ ...value, tds_section_code: code, tds_rate: rate, tds_override_reason: "" });
  };

  // "NONE · No TDS" short-circuits the auto-rate (bug fix 2026-02-11): the
  // no-PAN 20% fallback only kicks in when a REAL TDS section is chosen,
  // never for NONE. Keeps the summary panel clean for Accounts users.
  const isNoneSection = !value.tds_section_code || value.tds_section_code === "NONE";
  const chosenSection = tdsSections.find(x => x.code === value.tds_section_code);
  const autoRate = isNoneSection ? 0 : (chosenSection ? (
    !vendor?.pan ? (chosenSection.rate_no_pan || 20) :
    (vendor?.vendor_type === "Individual" || vendor?.vendor_type === "HUF") ? (chosenSection.rate_individual_huf || 0) :
    (chosenSection.rate_others || 0)
  ) : 0);
  const overrideActive = !isNoneSection && Math.abs((parseFloat(value.tds_rate || 0)) - autoRate) > 0.01;
  const singleThreshold = chosenSection?.threshold_single || 0;
  const belowSingle = singleThreshold > 0 && totals.sub < singleThreshold;

  return (
    <div className="space-y-4">
      {!compactHeader && vendor && (
        <div className="flex gap-2 items-center text-xs">
          <Badge variant="outline" data-testid="tb-gst-type">{split === "CGST_SGST" ? "Intra-state · CGST + SGST" : split === "IGST" ? "Inter-state · IGST" : "No GST (unregistered vendor)"}</Badge>
          <Badge variant="outline" data-testid="tb-vendor-state">Vendor state: {vendor.state || "—"}</Badge>
          <Badge variant="outline" data-testid="tb-place-supply">Place of supply: {place}</Badge>
        </div>
      )}

      {/* Invoice-level GST selector */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="qv-gst">
        <Label className="mb-0">Invoice GST %</Label>
        <Select value={String(invoiceGst)} onValueChange={(v) => applyInvoiceGst(parseFloat(v))} disabled={!gstReg}>
          <SelectTrigger className="w-32" data-testid="tb-invoice-gst"><SelectValue /></SelectTrigger>
          <SelectContent>
            {gstRates.map(g => <SelectItem key={g.rate} value={String(g.rate)}>{g.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {!gstReg && <span className="text-xs text-slate-500">Vendor is not GST-registered — locked at 0%</span>}
      </div>

      {/* Line items */}
      <div className="space-y-1">
        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 uppercase">
          <div className="col-span-5">Description</div><div className="col-span-1 text-right">Qty</div><div className="col-span-2 text-right">Rate</div><div className="col-span-2 text-right">Amount</div><div className="col-span-1 text-right">GST %</div><div className="col-span-1" />
        </div>
        {(value.lines || []).map((ln, i) => (
          <div key={i} className="grid grid-cols-12 gap-2" data-testid={`tb-line-${i}`}>
            <Input className="col-span-5" placeholder="Line description" value={ln.description} onChange={(e) => updLine(i, "description", e.target.value)} data-testid={i === 0 ? "qv-desc" : `tb-line-desc-${i}`} />
            <Input className="col-span-1 text-right" type="number" placeholder="0" value={ln.qty || ""} onChange={(e) => updLine(i, "qty", e.target.value)} data-testid={`tb-line-qty-${i}`} />
            <Input className="col-span-2 text-right" type="number" placeholder="0" value={ln.rate || ""} onChange={(e) => updLine(i, "rate", e.target.value)} data-testid={`tb-line-rate-${i}`} />
            <Input className="col-span-2 text-right font-mono" type="number" value={ln.amount || ""} onChange={(e) => updLine(i, "amount", e.target.value)} data-testid={`tb-line-amount-${i}`} />
            <Input className="col-span-1 text-right" type="number" value={ln.gst_rate || 0} onChange={(e) => updLine(i, "gst_rate", e.target.value)} disabled={!gstReg} data-testid={`tb-line-gst-${i}`} />
            <Button type="button" size="icon" variant="ghost" className="col-span-1" onClick={() => rmLine(i)} data-testid={`tb-line-rm-${i}`}><X className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="tb-add-line"><Plus className="w-3 h-3 mr-1" /> Line</Button>
      </div>

      {/* TDS — Accounts-only. Submitters (non-Accounts roles) see a
          placeholder note; the server also strips any TDS values they send. */}
      {tdsReadonly ? (
        <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="tb-tds-readonly">
          TDS will be applied by Accounts during verification.
        </div>
      ) : (
      <div className="grid grid-cols-4 gap-3">
        <div data-testid="qv-tds">
          <Label>TDS Section</Label>
          <Select value={value.tds_section_code || "NONE"} onValueChange={onTdsSection}>
            <SelectTrigger data-testid="tb-tds-section"><SelectValue /></SelectTrigger>
            <SelectContent>
              {tdsSections.map(s => <SelectItem key={s.code} value={s.code}>{s.code} · {s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>TDS %</Label>
          <Input type="number" step="0.01" value={value.tds_rate ?? 0} onChange={(e) => onChange({ ...value, tds_rate: parseFloat(e.target.value || 0) })} data-testid="tb-tds-rate" />
          <div className="text-[10px] text-slate-500 mt-0.5">Auto: {autoRate}%{overrideActive ? " · overridden" : ""}</div>
        </div>
        <div className="col-span-2">
          <Label>Override reason {overrideActive && <span className="text-rose-600 text-xs">(required)</span>}</Label>
          <Input value={value.tds_override_reason || ""} onChange={(e) => onChange({ ...value, tds_override_reason: e.target.value })} disabled={!overrideActive} placeholder="Why does the rate differ from the section's auto rate?" data-testid="tb-tds-override-reason" />
        </div>
      </div>
      )}
      {!tdsReadonly && belowSingle && chosenSection?.code !== "NONE" && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="tb-below-threshold-warning">
          Sub-total ₹{totals.sub.toLocaleString()} is below {chosenSection.code}'s single-bill threshold ₹{singleThreshold.toLocaleString()} — deducting TDS anyway per your entry.
        </div>
      )}

      {/* Live summary */}
      <Card className="bg-slate-50 border-slate-200"><CardContent className="p-3 text-sm space-y-1" data-testid="tb-summary">
        <Row label="Sub-total (Taxable value)" value={rupee(totals.sub)} bold />
        {split === "CGST_SGST" && <><Row label="CGST" value={rupee(totals.cgst)} /><Row label="SGST" value={rupee(totals.sgst)} /></>}
        {split === "IGST" && <Row label="IGST" value={rupee(totals.igst)} />}
        <Row label="Total GST" value={rupee(totals.totalGst)} />
        <Row label="Invoice Total" value={rupee(totals.invoiceTotal)} bold data-testid="tb-invoice-total" />
        <div className="border-t border-slate-300 my-1" />
        {!tdsReadonly && (
          <Row label={`Less: TDS @ ${value.tds_rate || 0}% u/s ${value.tds_section_code || "—"}`} value={`− ${rupee(totals.tdsAmount)}`} tone="amber" />
        )}
        {!tdsReadonly && (
          <Row label="Round-off (±₹1, optional)" input={<Input type="number" step="0.01" min="-1" max="1" value={value.round_off || 0} onChange={(e) => onChange({ ...value, round_off: parseFloat(e.target.value || 0) })} className="w-24 h-7 text-right" data-testid="tb-round-off" />} />
        )}
        <div className="border-t border-slate-300 my-1" />
        {tdsReadonly ? (
          <Row label="Net payable" value="to be confirmed by Accounts" tone="slate" data-testid="tb-net-payable-pending" />
        ) : (
          <Row label="Net Amount Payable to Vendor" value={rupee(totals.netPayable)} bold tone="emerald" data-testid="tb-net-payable" />
        )}
        <Row label="Charged to budget" value={rupee(totals.invoiceTotal)} tone="slate" data-testid="tb-budget-charge" />
      </CardContent></Card>
    </div>
  );
}

function Row({ label, value, input, bold, tone, "data-testid": testId }) {
  const toneClass = tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-600" : "text-slate-900";
  return (
    <div className="flex justify-between items-center gap-2">
      <span className={`text-xs ${bold ? "font-semibold text-slate-800" : "text-slate-500"}`}>{label}</span>
      {input ? input : <span className={`font-mono ${bold ? "font-bold" : ""} ${toneClass}`} data-testid={testId}>{value}</span>}
    </div>
  );
}
